/**
 * LR Direct — CANONICAL milestone write layer.
 *
 * Every broker/carrier milestone mutation goes through this module:
 *   markStageDone      → set an operational milestone (stop or dispatch level)
 *   undoMilestoneCascade (re-exported from ./undoCascade)
 *   closeLoadManual    → explicit Close Load (POD not required, POD untouched)
 *   reopenLoad         → explicit Reopen Load (documents/notes/history kept)
 *
 * `direct_dispatches.status` is a derived denormalisation recomputed from the
 * remaining canonical milestones after every write — never a source of truth.
 */
import { supabase } from "@/integrations/supabase/client";
import type { DerivedStage } from "@/lib/lrDirect/deriveStages";
import { setStopStage, setDispatchStage, type DispatchStatus } from "@/hooks/useUpdateDispatchStatus";
import { legacyStatusForStages } from "@/lib/lrDirect/milestoneActions";
import { notifyClient } from "@/lib/notify";
import { shipperLoadLink } from "@/lib/lrDirect/notifyLinks";

export { undoMilestoneCascade } from "@/lib/lrDirect/undoCascade";

export interface MarkStageDoneArgs {
  stage: DerivedStage;
  dispatchId: string;
  loadId: string;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId?: string | null;
  loadNumber?: string | null;
}

/** Complete one operational milestone. Throws on any failure (no silent writes). */
export async function markStageDone({
  stage: s, dispatchId, loadId, actorId, counterpartyUserId, shipperUserId, loadNumber,
}: MarkStageDoneArgs): Promise<void> {
  if (s.kind === "broker_stop" && s.stopId && s.stopType && s.stopNumber && s.subKind) {
    await setStopStage({
      dispatchId, loadId, stopId: s.stopId, stopNumber: s.stopNumber, stopType: s.stopType,
      subKind: s.subKind, actorId, counterpartyUserId, shipperUserId, loadNumber,
    });
    return;
  }
  if (s.kind === "broker_dispatch") {
    await setDispatchStage({
      dispatchId, loadId, stageKey: s.key as "truck_en_route" | "in_transit", actorId,
      counterpartyUserId, shipperUserId, loadNumber,
    });
    return;
  }
  throw new Error("This stage is not actionable.");
}

/**
 * Manual close. Allowed after Delivered even with no POD on file.
 * Does NOT create/imply a POD — POD Uploaded stays its own document milestone.
 */
export async function closeLoadManual(args: {
  dispatchId: string;
  loadId: string;
  actorId: string;
  counterpartyUserId?: string | null;
  hasPod?: boolean;
  reason?: string;
}): Promise<void> {
  const { dispatchId, loadId, actorId, counterpartyUserId, hasPod, reason } = args;
  const nowIso = new Date().toISOString();

  const { data: updRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: "closed", updated_at: nowIso })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updRows || updRows.length === 0) {
    throw new Error("Close failed: no dispatch row was updated. You may not have permission.");
  }

  const { data: evRows, error: evErr } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: "status_closed",
      payload: {
        status: "closed",
        manual_close: true,
        pod_on_file: hasPod ?? false,
        reason: reason ?? "Closed manually by broker/carrier",
      },
    })
    .select("id");
  if (evErr) throw evErr;
  if (!evRows || evRows.length === 0) {
    throw new Error("Close failed: the audit event was not created.");
  }

  if (counterpartyUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: "📦 Load Update",
      body: "Load closed",
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: "closed", manual_close: true },
    });
  }
}

/**
 * Reopen a closed load so operational corrections/undo become available again.
 * Clears the closed workflow state ONLY — documents, notes and all historical
 * milestone rows/events are preserved (an existing POD stays "POD Uploaded").
 */
export async function reopenLoad(args: {
  dispatchId: string;
  loadId: string;
  actorId: string;
  stages: DerivedStage[];
  accepted: boolean;
  counterpartyUserId?: string | null;
  reason?: string;
}): Promise<DispatchStatus> {
  const { dispatchId, loadId, actorId, stages, accepted, counterpartyUserId, reason } = args;

  // 1. Clear the closed state events (both canonical + legacy close writes).
  const { error: delErr } = await supabase
    .from("direct_dispatch_events" as any)
    .delete()
    .eq("dispatch_id", dispatchId)
    .in("event_type", ["status_closed"])
    .select("id");
  if (delErr) throw delErr;

  // 2. Recompute the legacy status column from remaining canonical milestones.
  const remaining = stages.map((s) => (s.key === "closed" ? { ...s, status: "future" as const, at: null } : s));
  const nextStatus = legacyStatusForStages(remaining, { accepted, closed: false });

  const { data: updRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: nextStatus, updated_at: new Date().toISOString() })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updRows || updRows.length === 0) {
    throw new Error("Reopen failed: no dispatch row was updated. You may not have permission.");
  }

  // 3. Audit event.
  const { data: evRows, error: evErr } = await supabase
    .from("direct_dispatch_events" as any)
    .insert({
      dispatch_id: dispatchId,
      actor_id: actorId,
      event_type: "status_reopened",
      payload: { reverted_to: nextStatus, reason: reason ?? "Reopened for corrections" },
    })
    .select("id");
  if (evErr) throw evErr;
  if (!evRows || evRows.length === 0) {
    throw new Error("Reopen failed: the audit event was not created.");
  }

  if (counterpartyUserId) {
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: "dispatch_status_changed",
      inAppType: "dispatch_status_changed",
      title: "📦 Load Update",
      body: "Load reopened for corrections",
      link: shipperLoadLink(loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, status: nextStatus, reopened: true },
    });
  }

  return nextStatus;
}
